import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CONFIG } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Amenities", to: "/amenities" },
  { label: "Floor Plans", to: "/floor-plans" },
  { label: "Location", to: "/location" },
  { label: "Gallery", to: "/gallery" },
  { label: "Price", to: "/price" },
  {
    label: "NRI Hub",
    to: "/nri",
    children: [
      { label: "NRI Investment Centre", to: "/nri" },
      { label: "Why NRIs Should Invest", to: "/nri/why-invest" },
      { label: "FEMA & RBI Guidelines", to: "/nri/fema-rbi" },
      { label: "Home Loans for NRIs", to: "/nri/home-loans" },
      { label: "Taxation for NRIs", to: "/nri/taxation" },
      { label: "Buying Process", to: "/nri/buying-process" },
      { label: "Virtual Site Visit", to: "/nri/virtual-site-visit" },
      { label: "All NRI Pages", to: "/nri" },
    ],
  },
  {
    label: "Tools",
    to: "/emi-calculator",
    children: [
      { label: "EMI Calculator", to: "/emi-calculator" },
      { label: "Home Loan", to: "/home-loan" },
      { label: "Stamp Duty", to: "/stamp-duty" },
      { label: "Cost Sheet", to: "/cost-sheet" },
    ],
  },
  { label: "Blogs", to: "/blogs" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-white flex items-center justify-center p-1 flex-shrink-0">
            <img src={ASSETS.logo} alt="Provident Winworth" className="w-full h-full object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-serif-lux text-base md:text-lg tracking-tight whitespace-nowrap">Provident <span className="text-[color:var(--pcv-gold)]">Winworth</span></div>
            <div className="text-[9px] uppercase tracking-[0.24em] text-[color:var(--pcv-text-2)] whitespace-nowrap">Edappally · Kochi</div>
          </div>
        </Link>

        {/* Desktop */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-desktop">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <button
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="px-3 py-2 text-xs uppercase tracking-[0.18em] text-[color:var(--pcv-text)] hover:text-[color:var(--pcv-gold)] transition inline-flex items-center gap-1"
                >
                  {item.label} <ChevronDown className="w-3 h-3 opacity-70" />
                </button>
                <div className="absolute top-full left-0 pt-2 hidden group-hover:block min-w-[260px]">
                  <div className="glass-strong border border-[color:var(--pcv-border)] py-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block px-4 py-2 text-[13px] text-[color:var(--pcv-text)] hover:text-[color:var(--pcv-gold)] hover:bg-white/5 transition"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-xs uppercase tracking-[0.18em] transition ${
                    isActive ? "text-[color:var(--pcv-gold)]" : "text-[color:var(--pcv-text)] hover:text-[color:var(--pcv-gold)]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="nav-call"
            href={`tel:${CONFIG.phoneRaw}`}
            className="flex items-center gap-2 px-3 py-2 border border-[color:var(--pcv-gold)]/40 hover:border-[color:var(--pcv-gold)] hover:text-[color:var(--pcv-gold)] text-[color:var(--pcv-text)] transition"
          >
            <Phone className="w-4 h-4 text-[color:var(--pcv-gold)]" />
            <div className="leading-tight">
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--pcv-text-2)]">Call Now</div>
              <div className="text-xs font-medium whitespace-nowrap">{CONFIG.phone}</div>
            </div>
          </a>
          <Link to="/contact" className="btn-gold text-[11px] whitespace-nowrap" data-testid="nav-book-visit">
            Book Site Visit
          </Link>
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden text-[color:var(--pcv-text)] p-2" data-testid="nav-mobile-toggle" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="glass-strong border-l border-[color:var(--pcv-border)] w-[85vw] sm:w-[380px] p-0 text-[color:var(--pcv-text)]">
            <div className="p-6 border-b border-[color:var(--pcv-border)] flex items-center justify-between">
              <div className="font-serif-lux text-xl">Provident <span className="text-[color:var(--pcv-gold)]">Winworth</span></div>
              <button onClick={() => setOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <nav className="p-4 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto no-scrollbar">
              {NAV.map((it) => (
                <div key={it.label}>
                  <Link
                    to={it.to}
                    data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block px-3 py-3 text-sm uppercase tracking-[0.16em] hover:text-[color:var(--pcv-gold)]"
                  >
                    {it.label}
                  </Link>
                  {it.children && (
                    <div className="pl-4 pb-2 space-y-1">
                      {it.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-3 py-1.5 text-[13px] text-[color:var(--pcv-text-2)] hover:text-[color:var(--pcv-gold)]"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-6 space-y-3">
                <a href={`tel:${CONFIG.phoneRaw}`} className="btn-ghost w-full justify-center"><Phone className="w-4 h-4" /> Call {CONFIG.phone}</a>
                <a href={`https://wa.me/${CONFIG.whatsapp}`} target="_blank" rel="noreferrer" className="btn-gold w-full justify-center"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
